<template>
  <v-container fluid>
    <template v-if="maintain">
      <div class="d-flex justify-center align-center" style="height: 50vh;">
        <span style="font-size: 50px; color: #ff6d81">{{ pageMessage }}</span>
      </div>
    </template>
    <template v-else-if="!hasPermission">
      <div class="d-flex justify-center align-center" style="height: 50vh;">
        <v-card class="pa-6" elevation="3">
          <v-icon size="64" color="error" class="mb-4">mdi-lock</v-icon>
          <h2 class="text-center" style="color: #f44336;">ไม่มีสิทธิ์เข้าถึงหน้านี้</h2>
          <p class="text-center mt-2">กรุณาติดต่อผู้ดูแลระบบ</p>
        </v-card>
      </div>
    </template>
    <template v-else>
      <v-card elevation="4" class="pa-4 mb-4" style="border-radius: 12px;">
        <!-- Header -->
        <v-card-title class="d-flex align-center pb-4">
          <v-icon large color="primary" class="mr-3">mdi-clipboard-check-outline</v-icon>
          <span class="text-h5 font-weight-bold" style="color: #1976d2;">รายงานผลการตอบแบบสอบถาม</span>
        </v-card-title>

        <v-divider class="mb-4"></v-divider>

        <!-- Filters Section -->
        <v-card-text>
          <v-row dense>
            <!-- ประเภทโครงการ -->
            <v-col cols="12" md="3">
              <v-select
                v-model="selectedProjectType"
                :items="projectTypeOptions"
                item-text="text"
                item-value="value"
                label="ประเภทโครงการ"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-format-list-bulleted-type"
                @change="loadProjectData"
              ></v-select>
            </v-col>

            <!-- ปีงบประมาณ -->
            <v-col cols="12" md="2">
              <v-select
                v-model="selectedFiscalYear"
                :items="fiscalYearOptions"
                item-text="text"
                item-value="value"
                label="ปีงบประมาณ"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-calendar"
                @change="loadProjectData"
              ></v-select>
            </v-col>

            <!-- หน่วยงาน -->
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedDepartment"
                :items="departmentOptions"
                item-text="dep_name"
                item-value="dep_id"
                label="หน่วยงาน"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-office-building"
                @change="filterData"
              ></v-autocomplete>
            </v-col>

            <!-- สถานะ -->
            <v-col cols="12" md="2">
              <v-select
                v-model="selectedStatus"
                :items="statusOptions"
                item-text="status"
                item-value="id"
                label="สถานะ"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-flag"
                @change="filterData"
              ></v-select>
            </v-col>

            <!-- ปุ่มค้นหา -->
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn
                color="primary"
                @click="loadProjectData"
                :loading="isLoading"
                block
              >
                <v-icon left>mdi-magnify</v-icon>
                ค้นหา
              </v-btn>
            </v-col>
          </v-row>

          <!-- Search Box -->
          <v-row class="mt-3">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="searchText"
                label="ค้นหาโครงการ"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
                placeholder="พิมพ์รหัสหรือชื่อโครงการ..."
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-end align-center">
              <v-chip class="mr-2" color="primary" text-color="white" small>
                <v-icon left small>mdi-database</v-icon>
                ทั้งหมด: {{ filteredProjectData.length }} รายการ
              </v-chip>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider class="my-2"></v-divider>

        <!-- Data Table -->
        <v-card-text>
          <v-data-table
            :headers="tableHeaders"
            :items="filteredProjectData"
            :search="searchText"
            :loading="isLoading"
            loading-text="กำลังโหลดข้อมูล..."
            no-data-text="ไม่พบข้อมูล"
            no-results-text="ไม่พบข้อมูลที่ค้นหา"
            :items-per-page="15"
            :footer-props="{
              'items-per-page-options': [10, 15, 25, 50, 100],
              'items-per-page-text': 'แสดง',
              'page-text': '{0}-{1} จาก {2}'
            }"
            class="elevation-1"
            style="border-radius: 8px;"
          >
            <!-- Index Column -->
            <template #[`item.index`]="{ index }">
              <span class="font-weight-bold">{{ index + 1 }}</span>
            </template>

            <!-- Project Code -->
            <template #[`item.Project_code`]="{ item }">
              <v-chip small color="blue-grey lighten-4">
                {{ item.Project_code }}
              </v-chip>
            </template>

            <!-- Project Name with Round -->
            <template #[`item.full_Name`]="{ item }">
              <div>
                <span class="font-weight-bold" style="color: #1976d2;">{{ item.Project_name }}</span>
                <template v-if="item.hasRound && item.round">
                  <v-chip x-small color="purple" text-color="white" class="ml-2">
                    รุ่น {{ item.round }} {{ item.round_name || '' }}
                  </v-chip>
                </template>
                <div class="mt-1">
                  <span style="color: #666; font-size: 12px;">
                    <v-icon x-small>mdi-office-building</v-icon>
                    {{ item.dep_name || '-' }}
                  </span>
                </div>
              </div>
            </template>

            <!-- Status -->
            <template #[`item.IsPlanStatus`]="{ item }">
              <v-chip
                v-if="getStatusInfo(item.IsPlanStatus)"
                :color="getStatusInfo(item.IsPlanStatus).color"
                :text-color="getStatusInfo(item.IsPlanStatus).font_color || 'white'"
                small
              >
                {{ getStatusInfo(item.IsPlanStatus).status }}
              </v-chip>
            </template>

            <!-- Actions -->
            <template #[`item.actions`]="{ item }">
              <div class="d-flex flex-wrap" style="gap: 8px;">
                <!-- แบบสอบถาม -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="success"
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click="openSurvey(item)"
                      elevation="2"
                    >
                      <v-icon left small>mdi-clipboard-edit</v-icon>
                      แบบสอบถาม
                    </v-btn>
                  </template>
                  <span>เปิดแบบสอบถามความพึงพอใจ</span>
                </v-tooltip>

                <!-- ผลการตอบแบบสอบถาม -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="info"
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click="openSurveyResult(item)"
                      elevation="2"
                    >
                      <v-icon left small>mdi-chart-bar</v-icon>
                      ผลการตอบ
                    </v-btn>
                  </template>
                  <span>ดูผลการตอบแบบสอบถาม</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Loading Dialog -->
      <v-dialog v-model="dialogLoading" persistent width="300">
        <v-card color="primary" dark>
          <v-card-text class="text-center py-4">
            <v-progress-circular indeterminate color="white" class="mb-3"></v-progress-circular>
            <div>กำลังโหลดข้อมูล กรุณารอสักครู่...</div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import axios from "axios";
import ConstData from "../const/Const";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ReportSatisfaction",
  data() {
    return {
      // Page State
      maintain: false,
      pageMessage: 'ปิดปรับปรุงระบบชั่วคราว',
      hasPermission: false,
      isLoading: false,
      dialogLoading: false,

      // Filters
      selectedProjectType: '2', // Default: อบรม / เดินทางไปปฏิบัติงาน
      selectedFiscalYear: new Date().getFullYear(),
      selectedDepartment: null,
      selectedStatus: '0', // 0 = ทั้งหมด
      searchText: '',

      // Options
      projectTypeOptions: [
        { text: 'จัดโครงการ', value: '1' },
        { text: 'อบรม / เดินทางไปปฏิบัติงาน', value: '2' }
      ],
      fiscalYearOptions: [],
      statusOptions: [],
      departmentOptions: [],

      // Data
      projectDataRaw: [],
      projectDataTable: [],

      // Table Headers
      tableHeaders: [
        { text: 'ลำดับ', value: 'index', width: '70px', sortable: false, align: 'center' },
        { text: 'รหัส', value: 'Project_code', width: '150px' },
        { text: 'ชื่องาน / กิจกรรม', value: 'full_Name' },
        { text: 'สถานะ', value: 'IsPlanStatus', width: '150px', align: 'center' },
        { text: 'ทำรายการ', value: 'actions', width: '280px', sortable: false, align: 'center' }
      ],

      // URLs
    //   urlSurvey: "https://assess.medicine.psu.ac.th/assess_forms.php?assess_id=MTE0MQ==&vch=1",
    //   urlSurveyResult: "https://assess.medicine.psu.ac.th/assess_result_detail.php?assess_id=MTE0MQ==&vch=1"
      urlSurvey: "https://assess.medicine.psu.ac.th/assess_forms.php?assess_id=MTE0MQ==&vch=1",
      urlSurveyResult: "https://assess.medicine.psu.ac.th/assess_result_detail.php?assess_id=1141"
    };
  },
  computed: {
    ...mapGetters({
      MeData: 'Staff/getMeData',
      depart: 'Depart/getDepart',
      StatusProject: 'Project/getStatusProject',
      IgnoreMenuPermiss: 'Permission/getIgnoreMenuPermiss',
      PermissionAdmin: 'Permission/getPermission',
    }),
    filteredProjectData() {
      let data = [...this.projectDataTable];
      
      // Filter by department
      if (this.selectedDepartment) {
        data = data.filter(item => item.dep_id == this.selectedDepartment || item.Dep_Code == this.selectedDepartment);
      }
      
      // Filter by status
      if (this.selectedStatus && this.selectedStatus !== '0') {
        data = data.filter(item => item.IsPlanStatus == this.selectedStatus);
      }
      
      return data;
    }
  },
  methods: {
    ...mapActions({
      initMeData: 'Staff/initMeData',
      initDepartData: 'Depart/initDepartData',
      initStatusProject: 'Project/initStatusProject',
      getIgnoreMenuPermissData: 'Permission/getIgnoreMenuPermissData',
      getPermissionData: 'Permission/getPermissionData',
    }),

    // Initialize Page
    async init() {
      this.dialogLoading = true;
      
      try {
        // Load MeData
        await this.initMeData();
        
        if (this.MeData && this.MeData.length > 0) {
          // Check Permission
          await this.getIgnoreMenuPermissData({
            perid: this.MeData[0].perid,
            page_id: '27'
          });

          await this.getPermissionData({
            perid: this.MeData[0].perid
          });

          // Check permission_id == '19002'
          this.hasPermission = this.checkPermission();

          if (this.hasPermission) {
            // Load Department Data
            await this.initDepartData();
            this.departmentOptions = [{ dep_id: null, dep_name: 'ทั้งหมด' }, ...this.depart];

            // Load Status Project
            await this.initStatusProject();
            this.statusOptions = [{ id: '0', status: 'ทั้งหมด', color: 'grey' }, ...this.StatusProject];

            // Load Project Data
            await this.loadProjectData();
          }
        }
      } catch (error) {
        console.error('Init Error:', error);
      } finally {
        this.dialogLoading = false;
      }
    },

    // Check Permission
    checkPermission() {
      // Check if user has permission_id == '19002'
      if (this.IgnoreMenuPermiss && this.IgnoreMenuPermiss.length > 0) {
        const hasPermiss = this.IgnoreMenuPermiss.some(item => item.permission_id == '19002');
        if (hasPermiss) return true;
      }
      
      // Check Admin Permission
      if (this.PermissionAdmin && this.PermissionAdmin.length > 0) {
        const hasPermiss = this.PermissionAdmin.some(item => item.permission_id == '19002');
        if (hasPermiss) return true;
      }

      return false;
    },

    // Load Project Data
    async loadProjectData() {
      this.isLoading = true;
      this.projectDataRaw = [];
      this.projectDataTable = [];

      try {
        const response = await axios.post(ConstData.ConnectionHost + '/HrTrain2/serviceAPI.php', {
          queryName: 'getProjectForSatisfaction',
          param: btoa(
            encodeURIComponent(
              JSON.stringify({
                FiscalYear: this.selectedFiscalYear,
                Project_type: this.selectedProjectType,
                permiss_check: this.MeData[0].perid
              })
            )
          )
        });

        if (response.data && response.data.length > 0) {
          this.projectDataRaw = response.data;
          this.processProjectData();
        }
      } catch (error) {
        console.error('Load Project Error:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลได้'
        });
      } finally {
        this.isLoading = false;
      }
    },

    // Process Project Data - Handle Rounds
    processProjectData() {
      this.projectDataTable = [];
      
      this.projectDataRaw.forEach(project => {
        // Filter by approved status (IsPlanStatus >= 2)
        if (project.IsPlanStatus == '2' || project.IsPlanStatus == '5' || 
            project.IsPlanStatus == '6' || project.IsPlanStatus == '7') {
          
          if (project.IsRoundStatus == '0' || !project.round || project.round.length === 0) {
            // No rounds - add project directly
            this.projectDataTable.push({
              ...project,
              hasRound: false,
              Ref_id: 0,
              round: null,
              round_name: null
            });
          } else {
            // Has rounds - add each round as separate row
            project.round.forEach(roundItem => {
              this.projectDataTable.push({
                ...project,
                hasRound: true,
                Ref_id: roundItem.id || 0,
                round: roundItem.Round,
                round_name: roundItem.Round_name
              });
            });
          }
        }
      });
    },

    // Filter Data
    filterData() {
      // Trigger computed property recalculation
      this.$forceUpdate();
    },

    // Get Status Info
    getStatusInfo(statusId) {
      if (this.StatusProject && this.StatusProject.length > 0) {
        return this.StatusProject.find(item => item.id == statusId) || null;
      }
      return null;
    },

    // Open Survey Form
    openSurvey(item) {
      const projectCode = item.Project_code || '';
      const projectName = encodeURIComponent(item.Project_name || '');
      const uuid = item.Ref_id || 0;
      
      let roundInfo = '';
      if (item.hasRound && item.round) {
        roundInfo = encodeURIComponent(' รุ่น ' + item.round + ' ' + (item.round_name || ''));
      }

      const url = `${this.urlSurvey}&vproject=${projectCode}&vname=${projectName}${roundInfo}&uuid=${uuid}`;
      window.open(url, '_blank');
    },

    // Open Survey Result
    openSurveyResult(item) {
      const projectCode = item.Project_code || '';
      const projectName = encodeURIComponent(item.Project_name || '');
      const uuid = item.Ref_id || 0;

      let roundInfo = '';
      if (item.hasRound && item.round) {
        roundInfo = encodeURIComponent(' รุ่น ' + item.round + ' ' + (item.round_name || ''));
      }

      const url = `${this.urlSurveyResult}&vproject=${projectCode}&vname=${projectName}${roundInfo}&uuid=${uuid}`;
      window.open(url, '_blank');
    },

    // Initialize Fiscal Year Options
    initFiscalYearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = 2023; // 2566 - 543
      
      // Add "ทั้งหมด" option
      this.fiscalYearOptions.push({ text: 'ทั้งหมด', value: '0' });
      
      // Add years from 2566 to current year + 1
      for (let year = startYear; year <= currentYear + 1; year++) {
        this.fiscalYearOptions.push({
          text: `${year + 543}`, // Convert to Buddhist Era
          value: year.toString()
        });
      }
      
      // Set default to current year
      this.selectedFiscalYear = currentYear;
    }
  },
  mounted() {
    // Initialize fiscal year options
    this.initFiscalYearOptions();
    
    // Initialize page
    this.init();
  }
};
</script>

<style scoped>
.v-card {
  border: none;
}

.v-data-table {
  border-radius: 8px;
}

.v-data-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.v-data-table >>> .v-data-table-header th {
  font-weight: bold !important;
  color: #333 !important;
}

.v-btn {
  text-transform: none;
}

.v-chip {
  font-weight: 500;
}
</style>
